// src/sections/MacroSection.jsx
import React, { useEffect, useRef, useState } from "react";
import MetricCard from "../components/MetricCard";
import ChartModal from "../components/ChartModal";
import MacroTrendsChart from "../components/MacroTrendsChart";
import "../components/Overview.css";
import { apiUrl } from "../apiBase";

// Config for all macro metrics – ids MUST match backend ids
const MACRO_METRICS = {
  // Snapshot block
  cpi_yoy: {
    id: "cpi_yoy",
    title: "CPI (YoY)",
    subtitle: "Headline inflation",
    icon: "📈",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1NsaA",
    info:
      "CPI looks at how a big basket of consumer prices has changed versus a year ago. It includes food and energy, so it can jump around when gas or groceries spike. If CPI stays far above 2% and does not slow, central banks feel pressure to keep rates higher for longer.",
  },
  core_cpi_yoy: {
    id: "core_cpi_yoy",
    title: "Core CPI (YoY)",
    subtitle: "Ex food & energy",
    icon: "📉",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1NqZA",
    info:
      "Core CPI removes food and energy, which are noisy month to month. It tries to show the underlying trend. If headline CPI is still high but core CPI is drifting lower, that usually makes markets less worried about aggressive rate hikes.",
  },
  unemployment_rate: {
    id: "unemployment_rate",
    title: "Unemployment Rate",
    subtitle: "Share of labor force without jobs",
    icon: "🧑‍💼",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O4og",
    info:
      "The unemployment rate counts people who do not have a job, are available to work, and have looked for work recently. If someone gives up searching, they drop out of the labor force and are no longer counted as unemployed. This is why the rate can fall even when people are still struggling to find work.",
  },
  real_gdp_growth: {
    id: "real_gdp_growth",
    title: "Real GDP Growth",
    subtitle: "Latest quarter, annualized",
    icon: "📊",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1MCaC",
    info:
      "Real GDP is the size of the economy adjusted for inflation. The growth rate here is quarter over quarter, annualized. A positive but slowing number can still be fine for markets. Repeated negative quarters often show up around recessions.",
  },
  pce_yoy: {
    id: "pce_yoy",
    title: "PCE (YoY)",
    subtitle: "Fed preferred inflation gauge",
    icon: "🧾",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1N5Xl",
    info:
      "PCE inflation measures the prices of what households actually spend money on. It adjusts for shifting behavior, like trading down to cheaper brands. The Fed pays more attention to PCE than CPI. When PCE moves closer to 2%, it usually supports a softer stance on rates.",
  },
  core_pce_yoy: {
    id: "core_pce_yoy",
    title: "Core PCE (YoY)",
    subtitle: "Ex food & energy",
    icon: "🧾",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1NkdI",
    info:
      "Core PCE strips out food and energy and is the single inflation number the Fed focuses on the most. If core PCE is clearly trending lower toward 2%, bonds and risk assets often respond positively because it reduces the need for tight policy.",
  },

  retail_sales_yoy: {
    id: "retail_sales_yoy",
    title: "Retail Sales (YoY)",
    subtitle: "Consumer spending growth",
    icon: "🛒",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1OcmR",
    info: "Spending at retail stores and online – a proxy for demand.",
  },
  housing_starts: {
    id: "housing_starts",
    title: "Housing Starts (SAAR)",
    subtitle: "Annualized rate",
    icon: "🏠",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1Mpk8",
    info: "New privately owned housing units started at an annual rate.",
  },

  // Added because SNAPSHOT_IDS includes it (prevents a missing card)
  m2_yoy: {
    id: "m2_yoy",
    title: "M2 (YoY)",
    subtitle: "Money supply growth",
    icon: "🏦",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O5G6",
    info:
      "M2 is a broad measure of money supply (cash, checking, savings, and money market funds). Faster growth can support spending and inflation; slower growth can tighten conditions over time.",
  },

  // Jobs & Consumers block
  nonfarm_payrolls_change: {
    id: "nonfarm_payrolls_change",
    title: "Nonfarm Payrolls",
    subtitle: "Jobs added (thousands)",
    icon: "📈",
    unit: "thousands",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O52Y",
    info:
      "This is the headline monthly jobs number. It shows how many net jobs were added or lost, usually in thousands. One noisy report does not mean much. A string of weak prints, especially combined with rising claims, is a clearer sign that the labor market is cooling.",
  },
  labor_force_participation: {
    id: "labor_force_participation",
    title: "Labor Force Participation",
    subtitle: "Share of adults working or seeking work",
    icon: "🧍‍♀️",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O46r",
    info:
      "Labor force participation tells you what percent of adults either have a job or are actively looking. Rising participation with low unemployment is a very strong combination.",
  },
  initial_jobless_claims: {
    id: "initial_jobless_claims",
    title: "Initial Jobless Claims",
    subtitle: "New unemployment filings (weekly)",
    icon: "📄",
    unit: "thousands",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O37O",
    info:
      "Initial claims show how many people started a new unemployment benefits claim in a given week. Markets watch the trend rather than one data point. A slow grind higher in claims usually appears before a sharp rise in the unemployment rate.",
  },
  consumer_sentiment: {
    id: "consumer_sentiment",
    title: "Consumer Sentiment",
    subtitle: "U. Michigan index",
    icon: "😊",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1O53a",
    info:
      "Consumer sentiment is based on a survey that asks households how they feel about their finances and the economy. People can feel gloomy but still spend money. When sentiment and spending both soften, that is a clearer warning sign.",
  },
};

const SNAPSHOT_IDS = [
  "cpi_yoy",
  "core_cpi_yoy",
  "unemployment_rate",
  "real_gdp_growth",
  "pce_yoy",
  "core_pce_yoy",
  "retail_sales_yoy",
  "housing_starts",
  "m2_yoy",
];

const JOBS_IDS = [
  "nonfarm_payrolls_change",
  "labor_force_participation",
  "initial_jobless_claims",
  "consumer_sentiment",
];

// Text-only modal for the "i" icon
const MacroInfoModal = ({ metric, onClose }) => {
  if (!metric) return null;

  return (
    <div className="modal-backdrop modal-open" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>How to read: {metric.title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close info">
            ×
          </button>
        </div>
        <div className="modal-description">
          {metric.info || "No macro explanation configured yet."}
        </div>
      </div>
    </div>
  );
};

const MacroSection = ({ onConnectionStatus }) => {
  const [macroData, setMacroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [macroHistory, setMacroHistory] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null); // FRED chart modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [infoMetric, setInfoMetric] = useState(null); // "i" modal

  // Keep latest callback without making useEffect depend on it (prevents fetch loops)
  const onConnectionStatusRef = useRef(onConnectionStatus);
  useEffect(() => {
    onConnectionStatusRef.current = onConnectionStatus;
  }, [onConnectionStatus]);

  useEffect(() => {
    const controller = new AbortController();

    const loadMacro = async () => {
      try {
        setLoading(true);

        const res = await fetch(apiUrl("/api/macro/overview"), {
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Failed to fetch macro data");

        const data = await res.json();

        const map = {};
        (data.metrics || []).forEach((m) => {
          map[m.id] = {
            value: m.latestValue ?? null,
            prevValue: m.prevValue ?? null,
            status: m.error ? "error" : "ok",
            timestamp: m.latestDate || null,
          };
        });

        setMacroData(map);
        setError(null);

        if (onConnectionStatusRef.current) onConnectionStatusRef.current(true);
      } catch (err) {
        if (controller.signal.aborted) return;

        console.error("Macro fetch error:", err);
        setError("Failed to load macro data");
        if (onConnectionStatusRef.current) onConnectionStatusRef.current(false);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadMacro();

    return () => {
      controller.abort();
    };
  }, []);

  const fetchMacroHistory = async () => {
  try {
    const res = await fetch(apiUrl("/api/macro-history"));
    if (!res.ok) throw new Error("Failed to fetch macro history");
    const json = await res.json();
    setMacroHistory(json);
  } catch (e) {
    console.error("Macro history error:", e);
    setMacroHistory(null);
  }
};

  useEffect(() => {
  fetchMacroHistory();
  const id = setInterval(fetchMacroHistory, 30 * 60 * 1000); // 30 min
  return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChartModal = (metricCfg) => {
    setActiveMetric(metricCfg);
    setIsModalOpen(true);
  };

  const closeChartModal = () => {
    setIsModalOpen(false);
    setActiveMetric(null);
  };

  const openInfoModal = (metricCfg) => {
    setInfoMetric(metricCfg);
  };

  const closeInfoModal = () => {
    setInfoMetric(null);
  };

  const SNAPSHOT_HISTORY_KEY = {
  cpi_yoy: "cpiYoY",
  core_cpi_yoy: "coreCpiYoY",
  unemployment_rate: "unemployment",
  real_gdp_growth: "realGdpGrowth",
  pce_yoy: "pceYoY",
  core_pce_yoy: "corePceYoY",
  retail_sales_yoy: "retailSalesYoY",
  housing_starts: "housingStarts",
  m2_yoy: "m2YoY",
};

  const renderMetricGrid = (ids, withSparklines = false) => (
  <div className="metrics-grid">
    {ids.map((id) => {
      const cfg = MACRO_METRICS[id];
      if (!cfg) return null;

      const historyKey = SNAPSHOT_HISTORY_KEY[id];
      const sparklineData = withSparklines && historyKey ? macroHistory?.[historyKey] : null;

      return (
        <MetricCard
          key={id}
          metric={cfg}
          data={macroData?.[id]}
          onClick={() => openChartModal(cfg)}
          onInfoClick={() => openInfoModal(cfg)}
          sparklineData={sparklineData}
          sparklineLabel={sparklineData ? "Last 12 pts" : undefined}
        />
      );
    })}
  </div>
);

  if (loading) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner" />
        <p>Loading macro data...</p>
      </div>
    );
  }

  return (
    <div className="overview">
      {/* Optional banner style matching your site (no layout changes) */}
      <div className="market-tone">
        <h3>Macro Data</h3>
        <p>High-level inflation, growth, and labor signals, pulled from live macro series.</p>
      </div>

      {error && (
        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>Heads up</h4>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Snapshot block */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Macro Snapshot</h2>
          <p>
            Quick read on inflation, growth, and demand. Click a card for the FRED chart. Hit the “i” for how to interpret it.
          </p>
        </div>

        {renderMetricGrid(SNAPSHOT_IDS, true)}

        <MacroTrendsChart macroData={macroData} />

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              Inflation near 2% with steady growth usually supports risk assets. If inflation re-accelerates, markets start pricing “higher for
              longer” policy. If growth and labor soften together, recession risk rises.
            </p>
          </div>
        </div>
      </section>

      {/* Jobs & Consumers block */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Jobs &amp; Consumers</h2>
          <p>Labor and household demand signals. These tend to turn before the broader economy does.</p>
        </div>

        {renderMetricGrid(JOBS_IDS)}

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              Payroll growth + stable claims usually means the economy can keep absorbing higher rates. Rising claims and weakening payrolls are a
              classic early warning that conditions are tightening.
            </p>
          </div>
        </div>
      </section>

      {/* FRED chart modal */}
      <ChartModal isOpen={isModalOpen} metric={activeMetric} onClose={closeChartModal} />

      {/* Info modal */}
      <MacroInfoModal metric={infoMetric} onClose={closeInfoModal} />
    </div>
  );
};

export default MacroSection;
